import React from 'react';
import Skeleton from 'react-loading-skeleton';
import styled, { css } from 'styled-components';
import { TooltipIcon, TooltipWrapper } from 'components';
import { Text } from 'components/Typography/Typography';
import { lineHeight, theme, toRem } from 'design';
import { DisbursementMethod, IPayee, IPayoutOverviewTotalsDisplay } from 'types';
import { calculateInstant, calculatePayroll, centsToDollarsDisplay, displayWithholding, getIncludedPayoutsCount } from 'utils';

const StyledText = styled(Text)<{ hasError?: boolean }>`
  font-size: ${toRem(theme.font.size.md)};
  line-height: ${lineHeight(theme.font.size.md)};

  ${({ hasError }) =>
    hasError &&
    css`
      color: ${theme.color.danger[50]};
    `}
`;

const TotalsGrid = styled.div<{ columnCount: number }>`
  display: grid;
  grid-template-columns: ${({ columnCount }) => `repeat(${columnCount}, minmax(0, 1fr))`};
  gap: ${toRem(theme.space.sm)};
  width: 100%;

  // Too narrow for columns: label/amount become rows.
  @media (max-width: ${theme.breakpoint.xsmall}px) {
    grid-template-columns: 1fr;

    & > div {
      flex-flow: row;
      justify-content: space-between;
      align-items: center;
    }
  }
`;

const TotalWrapper = styled.div`
  display: flex;
  flex-flow: column;
  gap: ${toRem(theme.space.xs)};

  & * {
    white-space: nowrap;
  }
`;

const GridItemHeader = styled.p`
  font-size: ${toRem(theme.font.size.xxs)};
  font-weight: ${theme.font.weight.medium};
  color: ${theme.color.neutral[500]};
`;

const HoldbackAmount = styled.div`
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  gap: ${toRem(theme.space.xs)};

  @media (max-width: ${theme.breakpoint.xsmall}px) {
    flex-wrap: nowrap;
  }
`;

interface IPayoutTotalsGridProps {
  payee: IPayee;
  includedPayouts: IPayoutOverviewTotalsDisplay;
  isLoading?: boolean;
  editHoldbackTrigger?: React.ReactNode;
}

export const PayoutTotalsGrid: React.FC<IPayoutTotalsGridProps> = ({ payee, includedPayouts, isLoading, editHoldbackTrigger }) => {
  const includedPayoutsCount = getIncludedPayoutsCount(includedPayouts);
  if (includedPayoutsCount <= 0) return null;

  const { employee, cash, withholdingAmount, holdbackAmount } = payee;
  const isInstant = employee?.disbursementMethod === DisbursementMethod.INSTANT;
  const instant = calculateInstant(payee);
  const payroll = calculatePayroll(payee);

  return (
    <TotalsGrid columnCount={includedPayoutsCount} data-cy="payee-totals-grid">
      {includedPayouts.cash && (
        <TotalWrapper data-cy="cash-total">
          <GridItemHeader>Cash</GridItemHeader>
          {isLoading ? <Skeleton width={90} /> : <StyledText fontWeight="medium">{cash ? centsToDollarsDisplay(cash) : '--'}</StyledText>}
        </TotalWrapper>
      )}

      {includedPayouts.payroll && (
        <TotalWrapper data-cy="payroll-net-total">
          <GridItemHeader>Payroll</GridItemHeader>
          {isLoading ? (
            <Skeleton width={90} />
          ) : (
            <TooltipWrapper>
              <StyledText fontWeight="medium" hasError={employee && payroll < 0}>
                {employee && payroll ? centsToDollarsDisplay(payroll) : '--'}
              </StyledText>
              {employee && withholdingAmount > 0 && (
                <TooltipIcon id={`withholding-tooltip-${payee.id}`} iconSize={18} dataCy="withholding-tooltip">
                  {displayWithholding(withholdingAmount)}
                </TooltipIcon>
              )}
            </TooltipWrapper>
          )}
        </TotalWrapper>
      )}

      {includedPayouts.holdbacks && (
        <TotalWrapper data-cy="holdback-total">
          <GridItemHeader>Holdback</GridItemHeader>
          {isLoading ? (
            <Skeleton width={90} />
          ) : isInstant ? (
            <HoldbackAmount>
              <StyledText fontWeight="medium">{centsToDollarsDisplay(holdbackAmount)}</StyledText>
              {(holdbackAmount !== 0 || instant !== 0) && editHoldbackTrigger}
            </HoldbackAmount>
          ) : (
            <StyledText fontWeight="medium">--</StyledText>
          )}
        </TotalWrapper>
      )}

      {includedPayouts.instant && (
        <TotalWrapper data-cy="instant-net-total">
          <GridItemHeader>Instant</GridItemHeader>
          {isLoading ? (
            <Skeleton width={90} />
          ) : (
            <StyledText fontWeight="medium" hasError={employee && instant < 0}>
              {isInstant ? centsToDollarsDisplay(instant) : '--'}
            </StyledText>
          )}
        </TotalWrapper>
      )}
    </TotalsGrid>
  );
};
